import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createEpicorJob,
  listEpicorJobs,
  cancelEpicorJob,
  getEpicorSummary,
  subscribeToEpicorJob,
  listEpicorCustomers,
  refreshBCCustomers,
} from '../../services/epicorApi';
import { listTagGroups } from '../../services/tagsApi';
import {
  Card,
  StatGrid,
  StatCard,
  Badge,
  StatusBadge,
  Button,
} from '../ui';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : (n ?? '—'));
const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');
const ALL_COMPANIES = ['AU', 'NZ'];
const PAGE_SIZE = 50;

/* ── Directory row helpers ── */
const LINK_CHIP = {
  matched: { label: 'Linked', cls: 'linked' },
  ambiguous: { label: 'Needs review', cls: 'review' },
};
const linkChipFor = (status) => LINK_CHIP[status] || { label: 'Not linked', cls: 'none' };

const subLineFor = (r) =>
  [
    r.email_address || 'No email on record',
    `#${r.cust_num}`,
    `${fmt(Number(r.shipto_count) || 0)} ${Number(r.shipto_count) === 1 ? 'ship-to' : 'ship-tos'}`,
    r.sc6_banner_group || null,
    (r.tags || []).length ? r.tags.map((t) => t.label).join(', ') : null,
  ]
    .filter(Boolean)
    .join(' · ');

const statusFor = (r) =>
  [r.inactive ? 'Inactive' : 'Active', r.is_present ? null : 'absent']
    .filter(Boolean)
    .join(' · ');

const Counter = ({ label, value, tone }) => (
  <StatCard label={label} value={fmt(Number(value) || 0)} tone={tone} />
);

/* ──────────────────────────────────────────────────────────────
   Main page
   ────────────────────────────────────────────────────────────── */
const CustomersPage = () => {
  const navigate = useNavigate();

  // Summary
  const [summary, setSummary] = useState([]);
  const [bcSnapshots, setBcSnapshots] = useState([]);

  // Directory state
  const [companyFilter, setCompanyFilter] = useState('');
  const [linkFilter, setLinkFilter] = useState('');
  // Tag spec ("trade_channel:swimart"), qualified so a value code shared by two
  // groups still means one thing.
  const [tagFilter, setTagFilter] = useState('');
  const [tagGroups, setTagGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState(null);
  const [rowsLoading, setRowsLoading] = useState(true);

  // Sync job controls
  const [companies, setCompanies] = useState(['AU', 'NZ']);
  const [dryRun, setDryRun] = useState(false);
  const [limit, setLimit] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [activeJobId, setActiveJobId] = useState(null);
  const [live, setLive] = useState(null);
  const [jobs, setJobs] = useState([]);
  const unsubRef = useRef(null);

  // BC refresh
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const data = await getEpicorSummary();
      setSummary(data.customers || []);
      setBcSnapshots(data.bc_snapshots || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const data = await listEpicorJobs({ limit: 20 });
      setJobs((data || []).filter((j) =>
        (j.stage_filter || []).includes('epicor_sync_customers')));
    } catch (e) { /* non-fatal */ }
  }, []);

  // Stale-response guard: only the latest request may write state.
  const reqIdRef = useRef(0);

  const loadRows = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setRowsLoading(true);
    try {
      setLoadError(null);
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (companyFilter) params.company = companyFilter;
      if (linkFilter) params.link = linkFilter;
      if (tagFilter) params.tag = tagFilter;
      if (search) params.search = search;
      const data = await listEpicorCustomers(params);
      if (reqId !== reqIdRef.current) return;
      setRows(data.customers || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      setLoadError(e.response?.data?.detail || e.message);
    } finally {
      if (reqId === reqIdRef.current) setRowsLoading(false);
    }
  }, [companyFilter, linkFilter, tagFilter, search, page]);

  useEffect(() => { loadRows(); }, [loadRows]);

  useEffect(() => {
    listTagGroups({ entity_type: 'customer' }).then(setTagGroups).catch(() => {});
  }, []);

  // Live search: 450ms debounce, 2-char minimum (empty clears the filter).
  useEffect(() => {
    const q = searchDraft.trim();
    if (q.length === 1) return undefined;
    const t = setTimeout(() => {
      setPage(0);
      setSearch(q);
    }, 450);
    return () => clearTimeout(t);
  }, [searchDraft]);
  useEffect(() => {
    loadSummary();
    loadJobs();
    const id = setInterval(() => { loadSummary(); loadJobs(); }, 5000);
    return () => clearInterval(id);
  }, [loadSummary, loadJobs]);

  // SSE for the active customers job.
  useEffect(() => {
    if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
    setLive(null);
    if (!activeJobId) return undefined;
    const unsub = subscribeToEpicorJob(
      activeJobId,
      (payload) => setLive(payload),
      () => { loadJobs(); loadSummary(); loadRows(); },
    );
    unsubRef.current = unsub;
    return () => { unsub(); unsubRef.current = null; };
  }, [activeJobId, loadJobs, loadSummary, loadRows]);

  const toggleCompany = (c) =>
    setCompanies((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (companies.length === 0) {
      setSubmitError('Select at least one company.');
      return;
    }
    setSubmitting(true);
    try {
      const { job_id } = await createEpicorJob({
        companies,
        dry_run: dryRun,
        limit: limit ? Number(limit) : null,
        stage: 'customers',
      });
      setActiveJobId(job_id);
      await loadJobs();
    } catch (err) {
      setSubmitError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const requestCancel = async (id) => {
    if (!window.confirm(`Request cancel for Epicor job #${id}?`)) return;
    try {
      await cancelEpicorJob(id);
      await loadJobs();
    } catch (e) {
      alert(`Cancel failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const refreshBC = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const res = await refreshBCCustomers(ALL_COMPANIES);
      setRefreshResult(res.results);
      await loadSummary();
      await loadRows();
    } catch (e) {
      alert(`BC refresh failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const activeRow = jobs.find((j) => j.id === activeJobId);
  const merged = { ...(activeRow || {}), ...(live || {}) };
  const counters = merged.counters || {};
  const isRunning = ['pending', 'running'].includes(merged.status);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Aggregate stats, honouring the company filter so cards and chips
  // never contradict the segmented control.
  const agg = summary
    .filter((r) => !companyFilter || r.company === companyFilter)
    .reduce(
      (a, r) => ({
        total: a.total + Number(r.total || 0),
        matched: a.matched + Number(r.matched || 0),
        ambiguous: a.ambiguous + Number(r.ambiguous || 0),
        importable: a.importable + Number(r.importable || 0),
      }),
      { total: 0, matched: 0, ambiguous: 0, importable: 0 },
    );

  const statDefs = [
    {
      id: '',
      value: fmt(agg.total),
      label: 'Staged customers',
      hint: companyFilter ? `From Epicor, ${companyFilter} only` : 'From Epicor, both companies',
    },
    {
      id: 'matched',
      value: fmt(agg.matched),
      label: 'Linked to the store',
      hint: 'Matched to a storefront record',
    },
    {
      id: 'ambiguous',
      value: fmt(agg.ambiguous),
      label: 'Need review',
      hint: 'More than one possible match',
      strong: true,
    },
    {
      id: 'unmatched',
      value: fmt(agg.importable),
      label: 'Not yet linked',
      hint: 'No storefront record found',
    },
  ];

  const chipDefs = [
    { id: '', label: 'All', count: fmt(agg.total) },
    { id: 'matched', label: 'Linked', count: fmt(agg.matched) },
    { id: 'ambiguous', label: 'Needs review', count: fmt(agg.ambiguous) },
    { id: 'unmatched', label: 'Not linked', count: fmt(agg.importable) },
  ];

  const anyFilter = Boolean(companyFilter || linkFilter || tagFilter || search);
  const resultNote =
    rowsLoading && rows.length > 0
      ? 'Updating…'
      : anyFilter
        ? 'Filtered view'
        : 'Sorted by customer ID';

  const clearFilters = () => {
    setSearchDraft('');
    setSearch('');
    setCompanyFilter('');
    setLinkFilter('');
    setTagFilter('');
    setPage(0);
  };

  const initialLoading = rowsLoading && rows.length === 0;
  const openRow = (r) => navigate(`/customers/${r.company}/${r.cust_num}`);

  return (
    <div className="customers-page oc-scope">
      {/* ── Header ── */}
      <div className="oc-header">
        <div className="oc-header-text">
          <h1 className="oc-title">Customers</h1>
          <p className="oc-blurb">
            Every Epicor customer staged in the middleware, with the storefront record it is
            linked to. Nothing is written back to the store in this phase.
          </p>
        </div>
        <button
          type="button"
          className="oc-btn oc-btn-secondary oc-header-action"
          disabled={refreshing}
          onClick={refreshBC}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20.5 12a8.5 8.5 0 0 1-14.6 5.9L3 15M3.5 12A8.5 8.5 0 0 1 18.1 6.1L21 9M21 4v5h-5M3 20v-5h5" />
          </svg>
          {refreshing ? 'Refreshing…' : 'Refresh from stores'}
        </button>
      </div>

      {/* ── Stats (click to filter the directory) ── */}
      <div className="oc-stats">
        {statDefs.map((s) => (
          <button
            key={s.id || 'all'}
            type="button"
            className={`oc-stat oc-blueprint${linkFilter === s.id ? ' on' : ''}${s.strong ? ' strong' : ''}`}
            onClick={() => { setPage(0); setLinkFilter(linkFilter === s.id ? '' : (s.id || '')); }}
            aria-pressed={linkFilter === s.id}
          >
            <i className="oc-corner tl" aria-hidden="true" />
            <i className="oc-corner tr" aria-hidden="true" />
            <i className="oc-corner bl" aria-hidden="true" />
            <i className="oc-corner br" aria-hidden="true" />
            <span className="oc-stat-value">{s.value}</span>
            <span className="oc-stat-label">{s.label}</span>
            <span className="oc-stat-hint">{s.hint}</span>
          </button>
        ))}
      </div>

      {/* ── Review banner ── */}
      {agg.ambiguous > 0 && linkFilter !== 'ambiguous' && (
        <div className="oc-review-banner">
          <span className="oc-review-headline">{fmt(agg.ambiguous)} need review</span>
          <span className="oc-review-copy">
            One Epicor customer matched more than one storefront record. Pick the right one and
            the rest of the sync unblocks.
          </span>
          <button
            type="button"
            className="oc-btn oc-btn-primary oc-review-action"
            onClick={() => { setPage(0); setLinkFilter('ambiguous'); }}
          >
            Show ambiguous
          </button>
        </div>
      )}

      {refreshResult && (
        <Card>
          <p className="muted" style={{ margin: 0 }}>
            {Object.entries(refreshResult).map(([store, r]) => (
              <span key={store} style={{ marginRight: 16 }}>
                <strong>{store}</strong>: {fmt(r.refresh?.fetched)} BC customers snapshotted;
                matched {fmt((r.rematch?.matched_custnum || 0) + (r.rematch?.matched_custid || 0) + (r.rematch?.matched_email || 0))},
                ambiguous {fmt(r.rematch?.ambiguous)}, unmatched {fmt(r.rematch?.unmatched)}.
              </span>
            ))}
          </p>
        </Card>
      )}

      {/* ── Active job ── */}
      {activeJobId && (
        <Card
          title={<>Active job #{activeJobId} <StatusBadge status={merged.status || 'pending'} /></>}
          actions={isRunning && (
            <Button variant="ghost" size="sm" onClick={() => requestCancel(activeJobId)}>Cancel</Button>
          )}
        >
          <StatGrid>
            <Counter label="Customers fetched" value={counters.customers_fetched} />
            <Counter label="Customers upserted" value={counters.customers_upserted} tone="success" />
            <Counter label="Ship-tos fetched" value={counters.shiptos_fetched} />
            <Counter label="Ship-tos upserted" value={counters.shiptos_upserted} tone="success" />
            <Counter label="Marked absent" value={counters.marked_absent} tone="warning" />
          </StatGrid>
          {merged.error_class && (
            <div className="error job-error">
              <strong>Error ({merged.error_class}):</strong> {merged.error_detail}
            </div>
          )}
        </Card>
      )}

      {/* ── Directory ── */}
      <div className="oc-frame oc-blueprint">
        <i className="oc-corner tl" aria-hidden="true" />
        <i className="oc-corner tr" aria-hidden="true" />
        <i className="oc-corner bl" aria-hidden="true" />
        <i className="oc-corner br" aria-hidden="true" />

        <div className="oc-filterbar">
          <div className="oc-field oc-field-search">
            <label htmlFor="cust-q">Search</label>
            <input
              id="cust-q"
              className="oc-input"
              type="text"
              placeholder="Name, customer ID, email or CustNum"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </div>
          <div className="oc-field">
            <span className="oc-field-label" id="oc-co-label">Company</span>
            <div className="oc-seg" role="radiogroup" aria-labelledby="oc-co-label">
              {['', ...ALL_COMPANIES].map((v) => (
                <label key={v || 'all'} className="oc-seg-opt">
                  <input
                    type="radio"
                    name="oc-co"
                    checked={companyFilter === v}
                    onChange={() => { setPage(0); setCompanyFilter(v); }}
                  />
                  <span>{v || 'All'}</span>
                </label>
              ))}
            </div>
          </div>
          {tagGroups.some((g) => (g.values || []).length > 0) && (
            <div className="oc-field" style={{ flex: 'none', minWidth: 180 }}>
              <label htmlFor="cust-tag">Tag</label>
              <select
                id="cust-tag"
                className="oc-input"
                value={tagFilter}
                onChange={(e) => { setPage(0); setTagFilter(e.target.value); }}
              >
                <option value="">Any tag</option>
                {tagGroups.map((group) => (
                  <optgroup key={group.id} label={group.label}>
                    {(group.values || []).map((value) => (
                      <option key={value.id} value={`${group.code}:${value.code}`}>
                        {value.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}
          <div className="oc-field oc-field-links">
            <span className="oc-field-label">Store link</span>
            <div className="oc-chips">
              {chipDefs.map((c) => (
                <button
                  key={c.id || 'all'}
                  type="button"
                  className={`oc-chip${linkFilter === c.id ? ' on' : ''}`}
                  onClick={() => { setPage(0); setLinkFilter(c.id); }}
                  aria-pressed={linkFilter === c.id}
                >
                  {c.label} · {c.count}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="oc-resultbar">
          <span className="oc-result-count">
            <strong>{fmt(total)}</strong>{' '}
            <span className="oc-muted">{total === 1 ? 'customer' : 'customers'}</span>
          </span>
          <span className="oc-muted oc-result-note">{resultNote}</span>
        </div>

        {loadError && <div className="oc-error" role="alert">{loadError}</div>}

        <div className="oc-table-wrap">
          <table className="oc-table">
            <caption className="sr-only">Staged Epicor customers</caption>
            <colgroup>
              <col className="oc-col-id" />
              <col className="oc-col-name" />
              <col className="oc-col-status" />
              <col className="oc-col-link" />
              <col className="oc-col-bcid" />
              <col className="oc-col-chev" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">Customer ID</th>
                <th scope="col">Name</th>
                <th scope="col">Status</th>
                <th scope="col">Store link</th>
                <th scope="col">Store ID</th>
                <th scope="col"><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {initialLoading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`skel-${i}`} className="oc-row-skel">
                    {['55%', '80%', '45%', '50%', '35%', '15%'].map((w, j) => (
                      <td key={j}><div className="ui-skel" style={{ height: 14, width: w }} /></td>
                    ))}
                  </tr>
                ))}
              {!initialLoading && rows.map((r) => {
                const chip = linkChipFor(r.match_status);
                const dimStatus = r.inactive || !r.is_present;
                return (
                  <tr
                    key={`${r.company}-${r.cust_num}`}
                    className="oc-row"
                    tabIndex={0}
                    onClick={() => openRow(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openRow(r);
                      }
                    }}
                  >
                    <td className="oc-custid">{r.cust_id || '—'}</td>
                    <td className="oc-namecell">
                      <div className="oc-name">{r.name || '—'}</div>
                      <div className="oc-sub">{subLineFor(r)}</div>
                    </td>
                    <td className={`oc-status${dimStatus ? ' dim' : ''}`}>{statusFor(r)}</td>
                    <td><span className={`oc-linkchip ${chip.cls}`}>{chip.label}</span></td>
                    <td className={`oc-bcid${r.bc_customer_id == null ? ' dim' : ''}`}>
                      {r.bc_customer_id ?? '—'}
                    </td>
                    <td className="oc-chev" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </td>
                  </tr>
                );
              })}
              {!initialLoading && rows.length === 0 && !loadError && (
                <tr>
                  <td colSpan={6}>
                    <div className="oc-empty">
                      <p className="oc-empty-title">Nothing matches that</p>
                      <p className="oc-empty-sub">Try a shorter search, or clear the filters.</p>
                      <button type="button" className="oc-btn oc-btn-secondary" onClick={clearFilters}>
                        Clear filters
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="oc-pager">
          <span className="oc-muted oc-pager-note">
            Page {page + 1} of {totalPages} · showing {fmt(rows.length)} of {fmt(total)}
          </span>
          <div className="oc-pager-btns">
            <button
              type="button"
              className="oc-btn oc-btn-secondary"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              ← Prev
            </button>
            <button
              type="button"
              className="oc-btn oc-btn-secondary"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* ── Sync controls ── */}
      <Card
        title="Run a customer sync"
        desc="Pull customers and ship-to addresses from Epicor Kinetic into staging and re-link them to BigCommerce customers."
      >
        <form onSubmit={submit} className="form-grid">
          <div className="form-group">
            <span className="form-label">Companies</span>
            <div className="checkbox-row">
              {ALL_COMPANIES.map((c) => (
                <label key={c} className="chip">
                  <input
                    type="checkbox"
                    checked={companies.includes(c)}
                    onChange={() => toggleCompany(c)}
                  />
                  {c}
                </label>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="chip">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              Dry run (fetch + count only, no writes)
            </label>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="epicor-cust-limit">
              Limit (optional, per company — disables mark-absent)
            </label>
            <input
              id="epicor-cust-limit"
              type="number"
              min="1"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="all"
            />
          </div>
          {submitError && <div className="banner error">{submitError}</div>}
          <div className="actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Enqueuing…' : 'Start customer sync'}
            </Button>
          </div>
        </form>
        <p className="muted" style={{ marginTop: 8 }}>
          BC snapshots:{' '}
          {bcSnapshots.length === 0
            ? 'none yet — use "Refresh BC customers".'
            : bcSnapshots
                .map((s) => `${s.store_code}: ${fmt(Number(s.total))} (${fmt(Number(s.with_custnum))} with CustNum, refreshed ${fmtTime(s.last_refreshed_at)})`)
                .join(' · ')}
        </p>

        <h3 className="section-subtitle">Staging summary by company</h3>
        <table>
          <thead>
            <tr>
              <th scope="col">Company</th><th scope="col">Customers</th><th scope="col">Present</th>
              <th scope="col">Inactive</th><th scope="col">Ship-tos</th><th scope="col">Linked to BC</th>
              <th scope="col">Ambiguous</th><th scope="col">Importable</th><th scope="col">Last synced</th>
            </tr>
          </thead>
          <tbody>
            {summary.length === 0 && (
              <tr><td colSpan={9} className="muted">No staged customers yet — run a sync above.</td></tr>
            )}
            {summary.map((row) => (
              <tr key={row.company}>
                <td><Badge variant="primary">{row.company}</Badge></td>
                <td>{fmt(Number(row.total))}</td>
                <td>{fmt(Number(row.present))}</td>
                <td>{fmt(Number(row.inactive))}</td>
                <td>{fmt(Number(row.shiptos))}</td>
                <td>{fmt(Number(row.matched))}</td>
                <td>{fmt(Number(row.ambiguous))}</td>
                <td><strong>{fmt(Number(row.importable))}</strong></td>
                <td className="muted">{fmtTime(row.last_synced_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* ── Recent jobs ── */}
      <Card title="Recent customer sync jobs">
        <table>
          <thead>
            <tr>
              <th scope="col">ID</th><th scope="col">Status</th><th scope="col">Companies</th>
              <th scope="col">Dry run</th><th scope="col">Customers</th><th scope="col">Ship-tos</th>
              <th scope="col">Absent</th><th scope="col">Created by</th><th scope="col">Finished</th><th scope="col" />
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr><td colSpan={10} className="muted">No customer sync jobs yet.</td></tr>
            )}
            {jobs.map((j) => {
              const c = j.counters || {};
              const comps = (j.payload && j.payload.companies) || [];
              const running = ['pending', 'running'].includes(j.status);
              return (
                <tr key={j.id}>
                  <td>
                    <button type="button" className="link" onClick={() => setActiveJobId(j.id)}>
                      #{j.id}
                    </button>
                  </td>
                  <td><StatusBadge status={j.status} /></td>
                  <td>{Array.isArray(comps) ? comps.join(', ') : '—'}</td>
                  <td>{j.dry_run ? 'yes' : 'no'}</td>
                  <td>{fmt(c.customers_upserted)}</td>
                  <td>{fmt(c.shiptos_upserted)}</td>
                  <td>{fmt(c.marked_absent)}</td>
                  <td className="muted">{j.created_by}</td>
                  <td className="muted">{fmtTime(j.finished_at)}</td>
                  <td>
                    {running && (
                      <Button variant="ghost" size="sm" onClick={() => requestCancel(j.id)}>Cancel</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default CustomersPage;
